
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isTutor } from '@/utils/roleValidation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { MessageCircle, Clock } from 'lucide-react';
import { pb, Conversation, Message } from '@/lib/pocketbase';
import { getProfilePictureUrl, getUserInitials } from '@/utils/profileUtils';

interface MessagesDropdownProps {
  buttonClassName?: string;
}

const MessagesDropdown = ({ buttonClassName }: MessagesDropdownProps = {}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const subscriptionRef = useRef<(() => void) | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      if (subscriptionRef.current) {
        subscriptionRef.current();
      }
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setConversations([]);
      setUnreadCount(0);
      return;
    }

    fetchConversations();
    setupMessageSubscription();
  }, [user?.id]);

  const setupMessageSubscription = async () => {
    if (!user?.id || subscriptionRef.current) return;

    try {
      const unsubscribe = await pb.collection('messages').subscribe('*', (e) => {
        // Refresh conversations when messages are created or updated
        if (e.action === 'create' || e.action === 'update') {
          // Debounce the refresh with longer delay and prevent stacking
          if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
          }
          fetchTimeoutRef.current = setTimeout(() => {
            if (!isFetchingRef.current) {
              fetchConversations();
            }
          }, 1000); // Increased debounce to 1 second
        }
      });

      subscriptionRef.current = unsubscribe;
    } catch (error) {
      // Silently fail - realtime is not critical, will rely on manual refresh
      console.warn('Message subscription failed, will use manual refresh');
    }
  };

  const fetchConversations = async () => {
    if (!user || isLoading || isFetchingRef.current) return;
    
    isFetchingRef.current = true;
    setIsLoading(true);
    
    try {
      // Get all messages where user is sender or receiver
      const allMessages = await pb.collection('messages').getList(1, 200, {
        filter: `senderId = "${user.id}" || receiverId = "${user.id}"`,
        sort: '-created',
        expand: 'senderId,receiverId',
      });

      if (!isMountedRef.current) return;

      // Get tutor profiles to map user IDs to tutor IDs and photos
      const tutorProfiles = await pb.collection('tutors').getList(1, 200, {
        requestKey: `tutor_profiles_${Date.now()}`,
      }).catch(() => ({ items: [] }));

      // Create mapping of user ID to tutor data (ID and photo)
      const userIdToTutorData = new Map<string, { id: string; photo: string; name: string }>();
      tutorProfiles.items.forEach((tutor: any) => {
        let photoUrl = '';
        if (tutor.profilePictureUrl && tutor.profilePictureUrl.trim()) {
          const url = tutor.profilePictureUrl.trim();
          if (url.startsWith('http://') || url.startsWith('https://')) {
            photoUrl = url;
          }
        } else if (tutor.profilePicture && tutor.profilePicture.trim()) {
          try {
            photoUrl = pb.files.getURL(tutor, tutor.profilePicture);
          } catch (error) {
            console.error('Error getting tutor photo URL:', error);
          }
        }
        
        userIdToTutorData.set(tutor.user, {
          id: tutor.id,
          photo: photoUrl,
          name: tutor.name || 'Unknown Tutor'
        });
      });

      // Collect all unique user IDs from messages for student profile lookup
      const uniqueUserIds = new Set<string>();
      allMessages.items.forEach((msg: any) => {
        if (msg.senderId !== user.id) uniqueUserIds.add(msg.senderId);
        if (msg.receiverId !== user.id) uniqueUserIds.add(msg.receiverId);
      });

      // Fetch student profiles for all participants
      let studentProfiles: any = { items: [] };
      if (uniqueUserIds.size > 0) {
        const userIdsArray = Array.from(uniqueUserIds);
        studentProfiles = await pb.collection('student_profiles').getList(1, 200, {
          filter: userIdsArray.map(id => `user="${id}"`).join(' || '),
          requestKey: `student_profiles_${Date.now()}`,
        }).catch(() => ({ items: [] }));
      }

      // Create mapping of user ID to student data (ID and photo)
      const userIdToStudentData = new Map<string, { id: string; photo: string; name: string }>();
      studentProfiles.items.forEach((student: any) => {
        let photoUrl = '';
        if (student.profilePictureUrl && student.profilePictureUrl.trim()) {
          const url = student.profilePictureUrl.trim();
          if (url.startsWith('http://') || url.startsWith('https://')) {
            photoUrl = url;
          }
        } else if (student.profilePicture && student.profilePicture.trim()) {
          try {
            photoUrl = pb.files.getURL(student, student.profilePicture);
          } catch (error) {
            console.error('Error getting student photo URL:', error);
          }
        }
        
        userIdToStudentData.set(student.user, {
          id: student.id,
          photo: photoUrl,
          name: student.name || 'Unknown Student'
        });
      });

      // Group messages by conversation partner
      const conversationMap = new Map<string, any>();
      
      allMessages.items.forEach((msg: any) => {
        // Determine the other participant
        const otherUserId = msg.senderId === user.id ? msg.receiverId : msg.senderId;
        const otherUser = msg.expand?.senderId?.id === otherUserId ? msg.expand.senderId : msg.expand?.receiverId;
        
        if (!otherUser) {
          return;
        }

        const messageContent = msg.message || '';
        const hasAttachment = msg.files && msg.files !== '';
        const displayMessage = messageContent || (hasAttachment ? 'Shared a file' : 'Empty message');
        
        // Get proper name and photo
        const tutorData = userIdToTutorData.get(otherUserId);
        const studentData = userIdToStudentData.get(otherUserId);
        
        // Prioritize tutor data first, then student data, then base user
        let participantName = otherUser.name || 'Unknown User';
        let participantAvatar = otherUser.avatar || '';
        
        if (tutorData) {
          participantName = tutorData.name;
          if (tutorData.photo && tutorData.photo.trim() !== '') {
            participantAvatar = tutorData.photo;
          }
        } else if (studentData) {
          participantName = studentData.name;
          if (studentData.photo && studentData.photo.trim() !== '') {
            participantAvatar = studentData.photo;
          }
        }
        
        // Only update if this is a newer message for this conversation
        if (!conversationMap.has(otherUserId) || new Date(msg.created) > conversationMap.get(otherUserId).lastMessageAt) {
          conversationMap.set(otherUserId, {
            id: otherUserId,
            participantId: otherUserId,
            participantName,
            participantAvatar,
            lastMessage: displayMessage,
            lastMessageAt: msg.created,
            expand: {
              studentId: msg.expand?.senderId?.id === otherUserId ? msg.expand.senderId : null,
              tutorId: msg.expand?.receiverId?.id === otherUserId ? msg.expand.receiverId : msg.expand?.senderId
            }
          });
        }
      });

      // Convert map to array and count unread messages
      const conversationsWithUnread: Conversation[] = [];
      let totalUnread = 0;
      
      for (const [participantId, conversation] of conversationMap.entries()) {
        try {
          const unreadMessages = await pb.collection('messages').getList(1, 1, {
            filter: `senderId = "${participantId}" && receiverId = "${user.id}" && isRead = false`,
          });
          
          const unreadForConv = unreadMessages.totalItems;
          conversationsWithUnread.push({
            ...conversation,
            unreadCount: unreadForConv
          } as Conversation);
          
          totalUnread += unreadForConv;
        } catch (error: any) {
          conversationsWithUnread.push({
            ...conversation,
            unreadCount: 0
          } as Conversation);
        }
      }
      
      // Sort by last message time
      conversationsWithUnread.sort((a, b) => 
        new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
      );
      
      if (isMountedRef.current) {
        setConversations(conversationsWithUnread);
        setUnreadCount(totalUnread);
      }
    } catch (error: any) {
      console.error('Error fetching conversations:', error);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  const getOtherUser = (conversation: Conversation) => {
    return isTutor(user) 
      ? conversation.expand?.studentId 
      : conversation.expand?.tutorId;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'now';
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString();
  };

  const handleConversationClick = (conversation: Conversation) => {
    // Navigate to messages page with the participant ID
    navigate('/messages', { 
      state: { 
        selectedParticipantId: conversation.participantId 
      } 
    });
    setIsOpen(false);
  };

  const handleViewAllMessages = () => {
    navigate('/messages');
    setIsOpen(false);
  };

  // Filter conversations for different tabs
  const allConversations = conversations;
  const unreadConversations = conversations.filter(conv => (conv.unreadCount || 0) > 0);

  const ConversationList = ({ conversations: convs }: { conversations: Conversation[] }) => (
    <div className="space-y-2 max-h-80 overflow-y-auto overscroll-contain">
      {isLoading ? (
        <div className="text-center py-6 text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm">Loading...</p>
        </div>
      ) : convs.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <MessageCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No conversations</p>
        </div>
      ) : (
        convs.map((conversation) => {
          return (
            <div
              key={conversation.id}
              className="p-3 hover:bg-gray-50 cursor-pointer rounded-lg border transition-colors select-none"
              onClick={() => handleConversationClick(conversation)}
            >
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  {conversation.participantAvatar ? (
                    <AvatarImage 
                      src={conversation.participantAvatar} 
                      alt={conversation.participantName || 'User'}
                    />
                  ) : null}
                  <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-sm">
                    {getUserInitials(conversation.participantName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900 truncate text-sm">
                      {conversation.participantName || 'Unknown User'}
                    </div>
                    <div className="flex items-center gap-2">
                      {(conversation.unreadCount || 0) > 0 && (
                        <Badge className="h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs bg-blue-500 text-white">
                          {(conversation.unreadCount || 0) > 9 ? '9+' : conversation.unreadCount}
                        </Badge>
                      )}
                      <div className="text-xs text-gray-500 flex-shrink-0">
                        {formatTime(conversation.lastMessageAt)}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 truncate mt-1">
                    {conversation.lastMessage}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={buttonClassName || "relative hover:bg-gray-100 transition-colors touch-manipulation"}
        >
          <MessageCircle className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 flex items-center justify-center text-[10px] bg-red-500 text-white border-2 border-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-96 p-0 bg-white border shadow-xl z-[9999]" 
        align="end" 
        side="bottom"
        sideOffset={8}
        avoidCollisions={true}
        collisionPadding={8}
      >
        <div className="p-4 border-b bg-white relative z-[10000]">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Messages</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleViewAllMessages}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 touch-manipulation"
            >
              View All
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="all" className="w-full relative z-[10000]">
          <TabsList className="grid w-full grid-cols-2 rounded-none border-b bg-gray-50 relative z-[10001]">
            <TabsTrigger value="all" className="flex items-center gap-2 text-sm touch-manipulation">
              <MessageCircle className="h-4 w-4" />
              All
            </TabsTrigger>
            <TabsTrigger value="unread" className="flex items-center gap-2 text-sm touch-manipulation">
              <Clock className="h-4 w-4" />
              Unread
              {unreadCount > 0 && (
                <Badge className="h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs bg-red-500 text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="p-4 mt-0 bg-white relative z-[10000]">
            <ConversationList conversations={allConversations} />
          </TabsContent>
          
          <TabsContent value="unread" className="p-4 mt-0 bg-white relative z-[10000]">
            <ConversationList conversations={unreadConversations} />
          </TabsContent>
        </Tabs>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MessagesDropdown;
